import clsx from 'clsx';
import { isEmpty } from 'lodash';
import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DatePicker, FormInput, FormSelect, Loader, RadioGroup } from 'components';
import { mailRegex, phoneRegex } from 'constants';
import { processString } from 'helpers';
import { useCivility, useFetch } from 'hooks';

const UserInfoWithoutMembership = () => {
  const { t } = useTranslation();
  const {
    formState: { errors, isValid },
    isLoading,
    submitClasses,
    control,
    setValue
  } = useFormContext();
  const { call: companiesCall, data: companiesOptions, setData: setCompanies } = useFetch({ withSetState: false });
  const { civilityOptions } = useCivility();

  useEffect(() => {
    getCompanies();
  }, []);

  const getCompanies = async () => {
    const { data } =
      (await companiesCall({
        service: 'castelis',
        method: 'get',
        path: '/repository/business'
      })) || {};

    if (!isEmpty(data?.body)) {
      const companiesOptions = data.body.map(({ id, name }) => ({ value: id, label: name }));
      setCompanies(companiesOptions);
    }
  };

  return (
    <>
      <h2 className="h3">{t('auth:registration.steps.informations')}</h2>

      <div className={clsx('grid grid-cols-12 gap-y-6 gap-x-2', isEmpty(errors) ? 'items-center' : 'items-start')}>
        {/* CIVILITY */}
        <div className="registration-label self-end col-span-4 lg:col-span-3">
          {t('form:civility.label')}&nbsp;<span className="text-primary-500 ml-1">*</span>
        </div>

        <div className="col-span-8 lg:col-span-9">
          <RadioGroup radioOptions={civilityOptions} rules={{ required: true }} row />
        </div>

        {/* LASTNAME & FIRSTNAME */}
        <div className="registration-label mt-0 col-span-4 lg:col-span-3">
          <p>
            {t('form:lastname')}&nbsp;<span className="text-primary-500 ml-1">*</span>
          </p>
          <p>
            {t('form:firstname')}&nbsp;<span className="text-primary-500 ml-1">*</span>
          </p>
        </div>

        <div className="col-span-8 flex gap-3 lg:col-span-9">
          <FormInput
            name="lastname"
            label={t('form:lastname')}
            rules={{ required: true }}
            placeholder={t('form:lastname')}
            className="w-1/2"
            dataTestId="userLastname"
            labelHidden
            onBlur={(value) => setValue('lastname', processString(value), { shouldValidate: true })}
          />

          <FormInput
            name="firstname"
            label={t('form:firstname')}
            rules={{ required: true }}
            placeholder={t('form:firstname')}
            dataTestId="userFirstname"
            className="w-1/2"
            labelHidden
            onBlur={(value) => setValue('firstname', processString(value), { shouldValidate: true })}
          />
        </div>

        {/* BIRTHDATE */}
        <div className="registration-label col-span-4 lg:col-span-3">
          {t('form:birthdate')}&nbsp;<span className="text-primary-500 ml-1">*</span>
        </div>

        <div className="col-span-8 lg:col-span-9">
          <Controller
            control={control}
            name="birthdate"
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <DatePicker
                selected={value}
                onChange={onChange}
                name="bithdate"
                placeholder="jj/mm/aaaa"
                peekNextMonth
                showMonthDropdown
                showYearDropdown
                scrollableYearDropdown
                yearDropdownItemNumber={40}
                openToDate={new Date('1970/01/01')}
                id="userBirthdate"
              />
            )}
          />
        </div>

        {/* EMAIL */}
        <div className="registration-label col-span-4 lg:col-span-3">
          {t('form:email')}&nbsp;<span className="text-primary-500 ml-1">*</span>
        </div>

        <div className="col-span-8 lg:col-span-9">
          <FormInput
            name="email"
            label={t('form:email')}
            rules={{
              required: true,
              pattern: {
                value: mailRegex,
                message: 'emailInvalid'
              }
            }}
            dataTestId="userEmail"
            type="email"
            labelHidden
          />
        </div>

        {/* MOBILE PHONE */}
        <div className="registration-label col-span-4 lg:col-span-3">
          {t('form:mobilePhone')}&nbsp;<span className="text-primary-500 ml-1">*</span>
        </div>

        <div className="col-span-8 lg:col-span-9">
          <FormInput
            name="mobilePhone"
            label={t('form:mobilePhone')}
            rules={{
              required: true,
              pattern: { value: phoneRegex, message: 'phoneInvalid' }
            }}
            dataTestId="userMobilePhone"
            type="tel"
            labelHidden
          />
        </div>

        {/* SPONSORSHIP CODE */}
        <div className="registration-label col-span-4 lg:col-span-3">{t('form:sponsorshipCode')}</div>

        <div className="col-span-8 lg:col-span-9">
          <FormInput
            name="sponsorshipCode"
            label={t('form:sponsorshipCode')}
            dataTestId="userSponsorshipCode"
            labelHidden
          />
        </div>

        {/* COMPANY */}
        <div className="registration-label col-span-4 lg:col-span-3">
          {t('form:company')}&nbsp;<span className="text-primary-500 ml-1">*</span>
        </div>

        <div className="col-span-8 lg:col-span-9">
          <Controller
            render={({ field: { onChange } }) => (
              <FormSelect
                options={companiesOptions || []}
                onChange={onChange}
                name="company"
                placeholder={t('form:placeholders.select')}
                id="userCompany"
              />
            )}
            name="company"
            control={control}
            rules={{ required: true }}
          />
        </div>

        {/* USER ID */}
        <div className="registration-label col-span-4 lg:col-span-3">
          {t('form:userId')}&nbsp;<span className="text-primary-500 ml-1">*</span>
        </div>

        <div className="col-span-8 lg:col-span-9">
          <FormInput
            name="userId"
            label={t('form:userId')}
            placeholder={t('form:placeholders.userId')}
            rules={{ required: true }}
            dataTestId="userId"
            labelHidden
          />
        </div>
      </div>

      <button type="submit" disabled={!isValid || isLoading} className={submitClasses} data-test-id="userSubmitBtn">
        {isLoading && <Loader loaderClass="w-4 h-4 text-white mr-2" />}
        {t('buttons.next')}
      </button>
    </>
  );
};

export default UserInfoWithoutMembership;
